import {
    RichDocSchema,
    isSafeUrl,
    richTextFromPlain,
    type RichDoc,
    type RichInlineNode,
    type RichMark,
    type RichParagraphNode,
} from "@edm/schema";

interface LooseNode {
    type?: string;
    text?: string;
    attrs?: Record<string, unknown>;
    marks?: { type?: string; attrs?: Record<string, unknown> }[];
    content?: LooseNode[];
}

function normalizeMarks(marks: LooseNode["marks"]): RichMark[] | undefined {
    if (!marks?.length) return undefined;
    const out: RichMark[] = [];
    for (const m of marks) {
        switch (m.type) {
            case "bold":
            case "italic":
            case "underline":
            case "strike":
                out.push({ type: m.type });
                break;
            case "link": {
                const href = String(m.attrs?.href ?? "");
                if (href && isSafeUrl(href)) out.push({ type: "link", attrs: { href } });
                break;
            }
            case "textStyle": {
                const color = m.attrs?.color;
                if (typeof color === "string" && /^#[0-9a-fA-F]{3,8}$/.test(color)) {
                    out.push({ type: "textStyle", attrs: { color } });
                }
                break;
            }
        }
    }
    return out.length > 0 ? out : undefined;
}

function normalizeInline(nodes: LooseNode[] | undefined): RichInlineNode[] {
    const out: RichInlineNode[] = [];
    for (const n of nodes ?? []) {
        if (n.type === "text" && typeof n.text === "string" && n.text.length > 0) {
            out.push({ type: "text", text: n.text, marks: normalizeMarks(n.marks) });
        } else if (n.type === "mergeTag") {
            const tag = String(n.attrs?.tag ?? "");
            if (/^[a-zA-Z0-9_.]+$/.test(tag)) {
                const fallback = n.attrs?.fallback;
                out.push({
                    type: "mergeTag",
                    attrs: {
                        tag,
                        ...(typeof fallback === "string" ? { fallback } : {}),
                    },
                });
            }
        } else if (n.type === "hardBreak") {
            out.push({ type: "hardBreak" });
        }
    }
    return out;
}

function normalizeParagraph(n: LooseNode): RichParagraphNode {
    const textAlign = n.attrs?.textAlign;
    return {
        type: "paragraph",
        ...(typeof textAlign === "string" &&
        ["left", "center", "right", "justify"].includes(textAlign)
            ? { attrs: { textAlign: textAlign as "left" } }
            : {}),
        content: normalizeInline(n.content),
    };
}

/**
 * Convert arbitrary Tiptap editor JSON into our constrained RichDoc subset,
 * dropping anything outside the schema (unknown nodes/marks/attrs). Falls
 * back to plain text extraction if the result still fails validation.
 */
export function normalizeTiptapJson(json: unknown): RichDoc {
    const root = json as LooseNode;
    const blocks: RichDoc["content"] = [];
    for (const block of root?.content ?? []) {
        if (block.type === "paragraph") {
            blocks.push(normalizeParagraph(block));
        } else if (block.type === "bulletList" || block.type === "orderedList") {
            const items = (block.content ?? [])
                .filter((li) => li.type === "listItem")
                .map((li) => ({
                    type: "listItem" as const,
                    content: (li.content ?? [])
                        .filter((p) => p.type === "paragraph")
                        .map(normalizeParagraph),
                }));
            if (items.length > 0) blocks.push({ type: block.type, content: items });
        }
        // anything else (headings, code blocks, tables from paste) is dropped
    }
    const doc: RichDoc = { type: "doc", content: blocks.length > 0 ? blocks : [{ type: "paragraph", content: [] }] };
    const parsed = RichDocSchema.safeParse(doc);
    if (parsed.success) return parsed.data;
    return richTextFromPlain(extractPlainText(root));
}

function extractPlainText(node: LooseNode | undefined): string {
    if (!node) return "";
    if (node.type === "text") return node.text ?? "";
    return (node.content ?? []).map(extractPlainText).join(" ");
}

/** RichDoc → Tiptap content JSON (identity shape; Tiptap accepts it as-is). */
export function richDocToTiptap(doc: RichDoc): Record<string, unknown> {
    return doc as unknown as Record<string, unknown>;
}
